<template>
  <div>
    <DateFilter
      v-model="selectedDate"
      class="mt-3"
      :default-filter="'today'"
      @changeDate="reCallData()"
    ></DateFilter>
    <v-layout justify-end align-content-end align-end>
      <v-btn outline color="primary" @click="showSearch = !showSearch"
        ><v-icon small>search</v-icon> {{ $t('Search') }}</v-btn
      >
      <v-btn outline color="primary" :loading="loading" @click="exportRankingReport()">{{
        $t('export')
      }}</v-btn>
    </v-layout>
    <v-card v-if="showSearch" class="mt-2 mb-2" style="padding: 12px">
      <v-layout row wrap class="form-control">
        <v-flex xs6 sm4 px-2>
          <div class="caption">{{ $t('priority') }}</div>
          <multiselect v-model="filter.priority" :options="priorityOptions" />
        </v-flex>
        <v-flex xs6 sm4 px-2>
          <div class="caption">{{ $t('emp_id') }}</div>
          <input v-model="filter.employee_id" type="text" :placeholder="$t('emp_id')" />
        </v-flex>
        <v-flex xs6 sm4 px-2>
          <div class="caption">{{ $t('reporter_name') }}</div>
          <input v-model="filter.username" type="text" :placeholder="$t('reporter_name')" />
        </v-flex>
        <v-flex xs6 sm4 px-2>
          <div class="caption">{{ $t('position') }}</div>
          <multiselect v-model="filter.position" :options="positionOptions" />
        </v-flex>
        <v-flex xs6 sm4 px-2>
          <div class="caption">{{ $t('subject') }}</div>
          <multiselect v-model="filter.topic" :options="subjectOptions" />
        </v-flex>
        <v-flex xs6 sm4 px-2>
          <div class="caption">{{ $t('complaint_type') }}</div>
          <multiselect v-model="filter.complaint_type" :options="typeOptions" />
        </v-flex>
        <v-flex xs6 sm4 px-2>
          <div class="caption">{{ $t('status') }}</div>
          <multiselect
            v-model="filter.complaint_status"
            :options="statusOptions"
            :custom-label="(option) => getStatusText(option)"
          />
        </v-flex>
        <v-flex xs6 sm4 px-2>
          <div class="caption">{{ $t('complaint_username') }}</div>
          <input
            v-model="filter.complaint_username"
            type="text"
            :placeholder="$t('complaint_username')"
          />
        </v-flex>
        <v-flex xs6 sm4 px-2>
          <div class="caption">{{ $t('complaint_department') }}</div>
          <multiselect v-model="filter.complaint_department" :options="departmentOptions" />
        </v-flex>
      </v-layout>
      <v-layout row wrap justify-end align-end align-content-end>
        <v-btn outline color="grey" @click="clearSearch">{{ $t('Clear Search') }}</v-btn>
        <v-btn depressed color="primary" @click="handleSearch">{{ $t('Search') }}</v-btn>
      </v-layout>
    </v-card>
    <v-pagination
      v-model="pageNumber"
      class="mt-3 mb-3"
      :length="pageLength"
      @input="changePage"
    ></v-pagination>
    <v-card>
      <v-data-table
        :loading="loading"
        hide-actions
        :headers="headers"
        :items="issueData"
        class="text-no-wrap"
      >
        <template #items="props">
          <tr style="cursor: pointer" @click="handleShowModalDetail(props.item)">
            <td>{{ props.item.id.padStart(6, '0') }}</td>
            <td>{{ props.item.timestamp | DateFormat('DD/MM/YYYY HH:mm') }}</td>
            <td>{{ props.item.priority }}</td>
            <td>{{ props.item.employee_id }}</td>
            <td>{{ [props.item.first_name, props.item.last_name].join(' ').trim() }}</td>
            <td>{{ props.item.position }}</td>
            <td>{{ props.item.topic }}</td>
            <td>{{ props.item.complaint_type }}</td>
            <td>{{ props.item.complaint_username }}</td>
            <td>{{ props.item.complaint_department }}</td>
            <td>
              <StatusSelect
                :value="props.item.complaint_status"
                @change="
                  (value, toggleLoading) => {
                    handleStatusChange(props.item, value, toggleLoading)
                  }
                "
              />
            </td>
          </tr>
        </template>
      </v-data-table>
    </v-card>
    <ModalReport
      v-if="showModalDetail && selectReport"
      :value="showModalDetail"
      :data="selectReport"
      @input="(value) => (showModalDetail = value)"
      @statusChange="handleStatusChange"
    />
  </div>
</template>

<script>
import moment from 'moment'
import DateFilter from '@/components/view/dashboard/DateFilter.vue'
import { mapGetters } from 'vuex'
import ModalReport from './ModalReport'
import tigerpolyApi from '@/api/tigerpoly'
import * as XLSX from 'xlsx'
import StatusSelect from './StatusSelect.vue'
import { handleApiError } from '@/api/util'
import Multiselect from 'vue-multiselect'

const baseFilter = {
  priority: '',
  employee_id: '',
  username: '',
  position: '',
  topic: '',
  complaint_type: '',
  complaint_status: '',
  complaint_username: '',
  complaint_department: ''
}

export default {
  components: { ModalReport, DateFilter, StatusSelect, Multiselect },
  data() {
    return {
      showSearch: false,
      pageNumber: 1,
      rowPerPage: 10,
      total: 0,
      loading: false,
      filter: { ...baseFilter },
      selectedDate: {
        start: moment().format('YYYY-MM-DD'),
        end: moment().format('YYYY-MM-DD')
      },
      issueData: [],
      totalAllReports: 0,
      headers: [
        { text: this.$t('issue_id'), value: 'id', sortable: false },
        { text: this.$t('Date'), value: 'timestamp', sortable: false },
        { text: this.$t('priority'), value: 'priority', sortable: false },
        { text: this.$t('emp_id'), value: 'employee_id', sortable: false },
        { text: this.$t('reporter_name'), value: 'full_name', sortable: false },
        { text: this.$t('reporter_position'), value: 'position', sortable: false },
        { text: this.$t('subject'), value: 'topic', sortable: false },
        { text: this.$t('complaint_type'), value: 'complaint_type', sortable: false },
        { text: this.$t('complaint_username'), value: 'complaint_username', sortable: false },
        { text: this.$t('complaint_department'), value: 'complaint_department', sortable: false },
        { text: this.$t('status'), value: 'status', sortable: false }
      ],
      showModalDetail: false,
      selectReport: null,
      positionOptions: [],
      departmentOptions: []
    }
  },
  computed: {
    ...mapGetters({
      selectedBot: 'selectedBot'
    }),
    limit() {
      return this.rowPerPage
    },
    offset() {
      return this.rowPerPage * this.pageNumber - this.rowPerPage
    },
    pageLength() {
      if (!this.total) return 0
      return Math.ceil(this.total / this.rowPerPage)
    },
    priorityOptions() {
      return ['สูง / High', 'กลาง / Medium', 'ต่ำ / Low']
    },
    statusOptions() {
      return ['pending', 'in_progress', 'completed']
    },
    subjectOptions() {
      return [
        'ผู้บริหารระดับสูง ประธาน / President',
        'รองประธาน ผู้บริหาร / Vice president, manager',
        'ผู้บริหารฝ่ายบุคคล / Manager HR&ADMIN',
        'เจ้าหน้าที่ฝ่ายบุคคล / Staff HR&ADMIN',
        'พนักงาน / Employee',
        'อื่นๆ / Others'
      ]
    },
    typeOptions() {
      return [
        'กระทำผิดกฎหมาย ระเบียบ และข้อบังคับบริษัท / Not follow the companies rules, redulations',
        'ฉ้อโกง/ทุจริต/คอรัปชั่น / Theft/fraud/corruption',
        'การใช้อำนาจแสวงหาผลประโยชน์โดยมิชอบ / Bribing or accepting bribes and any other benefits',
        'การบกพร่องต่อหน้าที่ การประพฤติตนไม่เหมาะสม / Misprision, inappropriate behavior',
        'การกลั่นแกล้งหรือการคุกคามที่ร้ายแรง รวมถึงพฤติกรรมคุกคาม / Serious bullying or harassment, including threatening behaviors',
        'อื่นๆ / Others'
      ]
    }
  },
  mounted() {
    this.init()
  },
  methods: {
    async init() {
      this.reCallData()
      this.fetchPositionOptions()
      this.fetchDepartmentOptions()
    },
    async reCallData() {
      try {
        const { data, total } = await this.fetchReport()
        if (data) {
          this.issueData = data
          this.total = total
        } else {
          this.issueData = []
          this.total = 0
        }
      } catch (error) {
        this.issueData = []
        this.total = 0
      }
    },
    async fetchPositionOptions() {
      try {
        const response = await tigerpolyApi.getPositions({
          params: {
            page_id: this.selectedBot.pageid
          }
        })

        if (response.status === 200) {
          this.positionOptions = response.data.data
            .map(({ name }) => name)
            .filter((name) => !!name)
            .sort((a, b) => a.localeCompare(b))
        }
      } catch (error) {
        console.error(error)
      }
    },
    async fetchDepartmentOptions() {
      try {
        const response = await tigerpolyApi.getDepartments({
          params: {
            page_id: this.selectedBot.pageid
          }
        })

        if (response.status === 200) {
          this.departmentOptions = response.data.data
            .map(({ name }) => name)
            .filter((name) => !!name)
            .sort((a, b) => a.localeCompare(b))
        }
      } catch (error) {
        console.error(error)
      }
    },
    handleShowModalDetail(reportData) {
      this.selectReport = reportData
      this.showModalDetail = true
    },
    changePage() {
      this.reCallData()
    },
    clearSearch() {
      this.filter = { ...baseFilter }
      this.reCallData()
    },
    handleSearch() {
      this.reCallData()
    },
    async fetchReport(isExport = false) {
      try {
        this.loading = true
        const config = {
          params: {
            page_id: this.selectedBot.pageid,
            datefrom: this.selectedDate.start,
            dateto: this.selectedDate.end,
            limit: isExport ? 999999 : this.limit,
            offset: isExport ? 0 : this.offset,
            ...this.filter
          }
        }
        const response = await tigerpolyApi.getReport(config)
        return response.data
      } catch (error) {
        console.error(error)
        this.$swal.fire({
          text: 'เรียกข้อมูลไม่สำเร็จ',
          type: 'error'
        })
        throw error
      } finally {
        this.loading = false
      }
    },
    async exportRankingReport() {
      try {
        const { data } = await this.fetchReport(true)
        const filename = 'issue_report'
        const rows = []
        rows.push(['Issue Report'])
        rows.push([
          `Filter Date From ${moment(this.selectedDate.start).format('DD/MM/YYYY')} - ${moment(
            this.selectedDate.end
          ).format('DD/MM/YYYY')}`
        ])
        rows.push([])
        rows.push([
          'Issue ID',
          'Date',
          'Priority',
          'Emp. ID',
          'Reporter name',
          'Reporter position',
          'Subject',
          'Type',
          'Name',
          'Department',
          'Complainment',
          'Status'
        ])
        for (let i = 0; i < data.length; i++) {
          const issueData = data[i]
          rows.push([
            issueData.id.padStart(6, '0'),
            issueData.timestamp ? moment(issueData.timestamp).format('DD/MM/YYYY HH:mm') : '-',
            issueData.priority || '-',
            issueData.employee_id,
            [issueData.first_name, issueData.last_name].join(' ').trim(),
            issueData.position,
            issueData.topic,
            issueData.complaint_type,
            issueData.complaint_username,
            issueData.complaint_department,
            issueData.detail,
            issueData.complaint_status || '-'
          ])
        }
        const wb = XLSX.utils.book_new()
        const dataWS = XLSX.utils.aoa_to_sheet(rows)
        XLSX.utils.book_append_sheet(wb, dataWS)
        XLSX.writeFile(wb, `${filename}.xlsx`)
      } catch (error) {
        this.$swal.fire({
          text: 'เรียกข้อมูลไม่สำเร็จ',
          type: 'error'
        })
        console.error(error)
      }
    },
    async handleStatusChange(item, value, toggleLoading) {
      try {
        toggleLoading()

        const response = await tigerpolyApi.putReportStatus({
          data: {
            id: item.id,
            page_id: item.page_id,
            complaint_status: value
          }
        })

        if (response.status === 204) {
          const itemIndex = this.issueData.findIndex(({ id }) => id === item.id)

          if (itemIndex === -1) return

          const newItem = { ...item, complaint_status: value }

          this.issueData.splice(itemIndex, 1, newItem)

          if (this.selectReport && this.selectReport.id === item.id) {
            this.selectReport = newItem
          }
        }
      } catch (error) {
        handleApiError(error)
      } finally {
        toggleLoading()
      }
    },
    getStatusText(value) {
      return {
        pending: this.$t('pending2'),
        in_progress: this.$t('in_progress'),
        completed: this.$t('completed')
      }[value]
    }
  }
}
</script>

<style></style>



// WEBPACK FOOTER //
// src/components/view/template/tigerpoly/reporting/issueReport/IssueReport.vue